import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ListFooter } from '../ui';
import { reportError } from '../utils/reportError';

// Ad-hoc Task Management — assignment, progress, audit (1–5 ★) and scoring.

const API_ORIGIN = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3000';
const fileHref = (u) => !u ? '#' : (/^https?:\/\//.test(u) ? u : (u.startsWith('/api/') ? API_ORIGIN + u : u));
const isImg = (u) => /\.(png|jpe?g|gif|webp)$/i.test(u || '');
const fmtDT = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';

const PRIORITIES = [
  { key: 'critical', label: '🔴 Critical', bg: '#FCEBEB', c: '#A32D2D' },
  { key: 'high', label: '🟠 High', bg: '#FEF0E6', c: '#C2410C' },
  { key: 'medium', label: '🟡 Medium', bg: '#FFF8E6', c: '#9A6B00' },
  { key: 'low', label: '🟢 Low', bg: '#EAF7EE', c: '#166534' },
];
const PRIO = Object.fromEntries(PRIORITIES.map(p => [p.key, p]));
const CATEGORIES = [['buyer_communication', 'Buyer Communication'], ['internal', 'Internal'], ['compliance', 'Compliance'], ['sampling', 'Sampling'], ['production', 'Production'], ['procurement', 'Procurement'], ['quality', 'Quality'], ['other', 'Other']];
const CAT = Object.fromEntries(CATEGORIES);
const STATUS_TAG = {
  pending: { bg: '#f0f2f8', c: '#666', label: 'Pending' },
  in_progress: { bg: '#EEEDF8', c: '#3B3A8F', label: 'In Progress' },
  completed: { bg: '#FFF8E6', c: '#9A6B00', label: 'Awaiting Audit' },
  audited: { bg: '#EAF7EE', c: '#166534', label: 'Audited ✓' },
  sent_back: { bg: '#FCEBEB', c: '#A32D2D', label: 'Sent Back' },
  cancelled: { bg: '#f0f0f0', c: '#999', label: 'Cancelled' },
};

const inputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };

// ── Voice input (Web Speech API) — additive dictation aid for text fields ──────
// Feature-detected: renders NOTHING where SpeechRecognition is unavailable (Firefox,
// older browsers), so the field stays a plain, fully-editable text input. Recognized
// speech is APPENDED via onAppend (never overwrites) — dictate, then keep typing/correcting.
const SpeechRec = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
if (typeof document !== 'undefined' && SpeechRec && !document.getElementById('mic-pulse-kf')) {
  const s = document.createElement('style'); s.id = 'mic-pulse-kf';
  s.textContent = '@keyframes micPulse{0%{box-shadow:0 0 0 0 rgba(226,45,45,.5)}70%{box-shadow:0 0 0 7px rgba(226,45,45,0)}100%{box-shadow:0 0 0 0 rgba(226,45,45,0)}}';
  document.head.appendChild(s);
}
function MicButton({ onAppend, title = 'Dictate (voice input)' }) {
  const [listening, setListening] = useState(false);
  const [hint, setHint] = useState('');
  const recRef = useRef(null);
  useEffect(() => () => { try { recRef.current && recRef.current.stop(); } catch (_) {} }, []); // stop on unmount
  if (!SpeechRec) return null; // unsupported → hide the button entirely (req 4)

  const toggle = () => {
    if (listening) { try { recRef.current && recRef.current.stop(); } catch (_) {} return; }
    setHint('');
    const rec = new SpeechRec();
    rec.lang = 'en-IN';           // Indian English — better for Indian names/accent (req 3)
    rec.continuous = true;        // keep listening until tapped again (req 2)
    rec.interimResults = false;   // only append finalized phrases
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      let t = '';
      for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) t += e.results[i][0].transcript;
      if (t.trim()) onAppend(t.trim());
    };
    rec.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setHint('Mic access needed — allow it in the browser.');
      else if (e.error !== 'no-speech' && e.error !== 'aborted') setHint('Voice input unavailable.');
      setListening(false);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (_) { setListening(false); }
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" onClick={toggle} title={listening ? 'Stop dictation' : title}
        style={{ border: 'none', cursor: 'pointer', borderRadius: '50%', width: 24, height: 24, fontSize: 12, lineHeight: 1, padding: 0,
          background: listening ? '#E22D2D' : '#EEEDF8', color: listening ? '#fff' : '#3B3A8F',
          animation: listening ? 'micPulse 1.4s infinite' : 'none' }}>🎤</button>
      {listening && <span style={{ fontSize: 10, color: '#E22D2D', fontWeight: 700, whiteSpace: 'nowrap' }}>● listening…</span>}
      {hint && <span style={{ fontSize: 10, color: '#A32D2D', whiteSpace: 'nowrap' }}>{hint}</span>}
    </span>
  );
}
const btnPrimary = { padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 };
const tag = (t) => ({ padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: t.bg, color: t.c, whiteSpace: 'nowrap' });

const ProgressBar = ({ pct }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
    <div style={{ width: 70, background: '#eee', borderRadius: 6, height: 8, overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: pct >= 100 ? '#16a34a' : '#3B3A8F' }} />
    </div>
    <span style={{ fontSize: 10, color: '#888' }}>{pct}%</span>
  </div>
);

const Stars = ({ value, onChange, size = 22 }) => (
  <div style={{ display: 'flex', gap: 2 }}>
    {[1, 2, 3, 4, 5].map(n => (
      <span key={n} onClick={() => onChange && onChange(n)}
        style={{ fontSize: size, cursor: onChange ? 'pointer' : 'default', color: n <= value ? '#f59e0b' : '#ddd' }}>★</span>
    ))}
  </div>
);

const DaysLeft = ({ t }) => {
  if (!t.deadline) return <span style={{ color: '#aaa' }}>—</span>;
  const done = ['completed', 'audited', 'cancelled'].includes(t.status);
  if (done) return <span style={{ color: '#888', fontSize: 11 }}>—</span>;
  const days = Math.ceil((new Date(t.deadline) - new Date()) / 86400000);
  if (days < 0) return <span style={{ color: '#A32D2D', fontWeight: 800 }}>{Math.abs(days)}d overdue</span>;
  if (days === 0) return <span style={{ color: '#C2410C', fontWeight: 800 }}>Due today</span>;
  return <span style={{ color: days <= 2 ? '#9A6B00' : '#166534', fontWeight: 600 }}>{days}d left</span>;
};

function FileUploadBtn({ onUploaded, multiple, label }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files]; if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append(multiple ? 'files' : 'file', f));
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(multiple ? (r.data.files || []).map(f => f.url) : [r.data.url]);
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <label style={{ ...btnPrimary, background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F', display: 'inline-block' }}>
      {busy ? 'Uploading…' : label}
      <input type="file" multiple={multiple} accept="image/*,.pdf,.xlsx,.xls,.doc,.docx" style={{ display: 'none' }} onChange={upload} />
    </label>
  );
}

const ProofThumbs = ({ urls }) => (urls || []).length === 0 ? null : (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
    {urls.map((u, i) => (
      <a key={i} href={fileHref(u)} target="_blank" rel="noreferrer">
        {isImg(u) ? <img src={fileHref(u)} alt="" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} />
          : <div style={{ width: 50, height: 50, borderRadius: 6, border: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, background: '#F7F6F3' }}>📄</div>}
      </a>
    ))}
  </div>
);

// ── Attachments — drag-and-drop + click-to-browse, mirrors backend allow-list ──
const ATTACH_EXT = ['pdf', 'png', 'jpg', 'jpeg', 'docx', 'xlsx', 'csv'];
const ATTACH_ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.csv';
const ATTACH_MAX = 10 * 1024 * 1024;
const fmtSize = (b) => b == null ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

// Validate a FileList client-side (instant feedback); backend re-validates.
const pickValidFiles = (list) => {
  const ok = [], errs = [];
  [...list].forEach(f => {
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!ATTACH_EXT.includes(ext)) errs.push(`${f.name}: type not allowed`);
    else if (f.size > ATTACH_MAX) errs.push(`${f.name}: exceeds 10 MB`);
    else ok.push(f);
  });
  return { ok, errs };
};

function AttachmentDropzone({ files, setFiles }) {
  const [drag, setDrag] = useState(false);
  const ref = useRef();
  const add = (list) => {
    const { ok, errs } = pickValidFiles(list);
    if (errs.length) alert('Some files were skipped:\n' + errs.join('\n'));
    if (ok.length) setFiles(prev => [...prev, ...ok]);
  };
  return (
    <div>
      <div onClick={() => ref.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={e => { e.preventDefault(); setDrag(false); }}
        onDrop={e => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        style={{ border: `2px dashed ${drag ? '#3B3A8F' : '#cfcfe6'}`, background: drag ? '#EEEDF8' : '#FBFBFE', borderRadius: 8, padding: '14px 12px', textAlign: 'center', cursor: 'pointer', fontSize: 12, color: '#666' }}>
        <div style={{ fontSize: 18 }}>📎</div>
        <div><strong style={{ color: '#3B3A8F' }}>Drag files here</strong> or click to browse</div>
        <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>PDF, PNG, JPG, JPEG, DOCX, XLSX, CSV · up to 10 MB each</div>
        <input ref={ref} type="file" multiple accept={ATTACH_ACCEPT} style={{ display: 'none' }} onChange={e => { add(e.target.files); e.target.value = ''; }} />
      </div>
      {files.length > 0 && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {files.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#F7F6F3', borderRadius: 6, padding: '5px 10px', fontSize: 12 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {f.name} <span style={{ color: '#999' }}>({fmtSize(f.size)})</span></span>
              <span onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} style={{ cursor: 'pointer', color: '#A32D2D', fontWeight: 700, flexShrink: 0 }} title="Remove">✕</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Authenticated download — fetch as blob (so the Bearer token is sent) then save.
const downloadAttachment = async (taskId, att) => {
  try {
    const r = await api.get(`/tasks/${taskId}/attachments/${att.id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url; a.download = att.file_name; document.body.appendChild(a); a.click();
    a.remove(); window.URL.revokeObjectURL(url);
  } catch (err) { alert('Download failed: ' + (err.response?.data?.error || err.message)); }
};

export default function Tasks() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMgmt = ['admin', 'management'].includes(user?.role);
  const [tab, setTab] = useState(isMgmt ? 'all' : 'my');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending_audit: 0, overdue: 0, my_pending: 0 });
  const [assignees, setAssignees] = useState([]);
  const [gpos, setGpos] = useState([]);
  const [filters, setFilters] = useState({});
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (tab !== 'all') qs.set('view', tab === 'my' ? 'my' : tab);
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const r = await api.get('/tasks?' + qs.toString()).catch(() => ({ data: { data: [] } }));
    setRows(r.data.data || []);
    const s = await api.get('/tasks/summary').catch(() => null);
    if (s?.data?.data) setCounts({
      pending_audit: s.data.data.team?.pending_audit || 0,
      overdue: s.data.data.team?.overdue || 0,
      my_pending: s.data.data.my?.pending || 0,
    });
  }, [tab, filters, isMgmt]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isMgmt) {
      api.get('/tasks/assignees').then(r => setAssignees(r.data.data || [])).catch(() => {});
      api.get('/oe/gpos').then(r => setGpos((r.data.data || []).filter(g => !g.is_superseded))).catch(() => {});
    }
  }, [isMgmt]);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const myTab = { key: 'my', label: '🙋 My Tasks', badge: counts.my_pending, blue: true, prominent: true };
  const tabs = isMgmt ? [
    { key: 'all', label: '📋 All Tasks' },
    { key: 'pending_audit', label: '🔍 Pending Audit', badge: counts.pending_audit },
    { key: 'overdue', label: '🚨 Overdue', badge: counts.overdue, red: true },
    myTab,
  ] : [myTab];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 14 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: '#1a1a2e' }}>✅ Task Manager</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Day-to-day task assignment, progress tracking, audit and scoring. Accepted task scores feed the appraisal (40% weightage).</p>
        </div>

        <div style={{ background: 'white', borderRadius: 10, boxShadow: 'none', border: '1px solid #E8E7E7', overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 14px', borderBottom: '1px solid #eee', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{
                  padding: '13px 16px', border: 'none', cursor: 'pointer',
                  fontSize: t.prominent ? 14 : 13,
                  background: t.prominent && tab !== t.key ? '#EEEDF8' : 'transparent',
                  borderRadius: t.prominent ? '8px 8px 0 0' : 0,
                  fontWeight: tab === t.key || t.prominent ? 700 : 400, color: tab === t.key ? '#3B3A8F' : t.prominent ? '#3B3A8F' : '#888',
                  borderBottom: tab === t.key ? '2px solid #3B3A8F' : '2px solid transparent', whiteSpace: 'nowrap' }}>
                  {t.label}
                  {t.badge > 0 && <span style={{ marginLeft: 6, padding: '1px 8px', borderRadius: 9, fontSize: 10, fontWeight: 800, background: t.red ? '#E22D2D' : t.blue ? '#3B3A8F' : '#9A6B00', color: 'white' }}>{t.badge}</span>}
                </button>
              ))}
            </div>
            {isMgmt && <button onClick={() => setShowNew(true)} style={btnPrimary}>+ New Task</button>}
          </div>

          <div style={{ padding: 18 }}>
            {tab === 'all' && isMgmt && (
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center', padding: '6px 8px', background: '#F7F6F3', borderRadius: 8 }}>
                {(() => { const fIn = { ...inputStyle, width: 'auto', minWidth: 0, flex: '0 0 auto' }; return (<>
                <select style={{ ...fIn, width: 140 }} value={filters.assigned_to || ''} onChange={e => setFilters({ ...filters, assigned_to: e.target.value })}>
                  <option value="">All assignees</option>{assignees.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                <select style={{ ...fIn, width: 130 }} value={filters.status || ''} onChange={e => setFilters({ ...filters, status: e.target.value })}>
                  <option value="">All statuses</option>{Object.entries(STATUS_TAG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
                <select style={{ ...fIn, width: 125 }} value={filters.priority || ''} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
                  <option value="">All priorities</option>{PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}</select>
                <select style={{ ...fIn, width: 150 }} value={filters.category || ''} onChange={e => setFilters({ ...filters, category: e.target.value })}>
                  <option value="">All categories</option>{CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                <input style={{ ...fIn, width: 90 }} placeholder="GPO…" value={filters.gpo_no || ''} onChange={e => setFilters({ ...filters, gpo_no: e.target.value })} />
                <input type="date" style={{ ...fIn, width: 125 }} title="From date" value={filters.from || ''} onChange={e => setFilters({ ...filters, from: e.target.value })} />
                <input type="date" style={{ ...fIn, width: 125 }} title="To date" value={filters.to || ''} onChange={e => setFilters({ ...filters, to: e.target.value })} />
                {Object.values(filters).some(Boolean) && <button onClick={() => setFilters({})} style={{ ...fIn, cursor: 'pointer', background: 'white', border: '1px solid #ddd' }}>✕ Clear</button>}
                </>); })()}
              </div>
            )}

            {tab === 'overdue' && rows.length > 0 && (
              <div style={{ background: '#FCEBEB', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#A32D2D', fontWeight: 700, fontSize: 13 }}>
                🚨 {rows.length} task(s) past deadline and not completed
              </div>
            )}

            {/* Pending Audit — card layout */}
            {tab === 'pending_audit' ? (
              rows.length === 0 ? <div style={{ padding: 30, textAlign: 'center', color: '#aaa' }}>No tasks awaiting audit. 🎉</div>
                : <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {rows.map(t => <AuditCard key={t.id} t={t} onDone={load} onOpen={() => setDetailId(t.id)} />)}
                </div>
            ) : tab === 'my' ? (
              <MyTasks rows={rows} onChanged={load} onOpen={setDetailId} />
            ) : (
              <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: tab === 'overdue' ? '#FCEBEB' : '#F7F6F3' }}>
                  {['Task No', 'Title', 'Assigned To', 'Priority', 'Category', 'Deadline', 'Days', 'Status', 'Progress', 'GPO'].map(h => (
                    <th key={h} style={{ padding: '9px 10px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '2px solid #eee', fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {rows.length === 0 ? <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No tasks{tab === 'overdue' ? ' overdue 🎉' : ' yet'}.</td></tr>
                    : rows.map(t => (
                      <tr key={t.id} onClick={() => setDetailId(t.id)} style={{ borderBottom: '1px solid #f5f5f5', cursor: 'pointer', background: tab === 'overdue' ? '#FFF8F8' : 'white' }}>
                        <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#3B3A8F' }}>{t.task_no}</td>
                        <td style={{ padding: '9px 10px', fontWeight: 600 }}>{t.title}</td>
                        <td style={{ padding: '9px 10px' }}>{t.assigned_to_name || '—'}</td>
                        <td style={{ padding: '9px 10px' }}><span style={tag(PRIO[t.priority] || PRIO.medium)}>{(PRIO[t.priority] || {}).label}</span></td>
                        <td style={{ padding: '9px 10px' }}>{CAT[t.category] || '—'}</td>
                        <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>{fmtDT(t.deadline)}</td>
                        <td style={{ padding: '9px 10px' }}><DaysLeft t={t} /></td>
                        <td style={{ padding: '9px 10px' }}><span style={tag(STATUS_TAG[t.status] || STATUS_TAG.pending)}>{(STATUS_TAG[t.status] || {}).label}</span></td>
                        <td style={{ padding: '9px 10px' }}><ProgressBar pct={t.progress_pct} /></td>
                        <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{t.gpo_no || '—'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <ListFooter shown={rows.length} total={rows.length} noun="tasks" />
              </>
            )}
          </div>
        </div>
      </div>

      {showNew && <NewTaskModal assignees={assignees} gpos={gpos} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
      {detailId && <TaskDetailModal id={detailId} user={user} isMgmt={isMgmt} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}

// Inline action row on every My Tasks card — progress slider, quick comment,
// proof upload (posted to the task's comment thread) and Mark Complete.
function TaskQuickActions({ t, onChanged, onComplete, onOpen }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const postComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try { await api.post(`/tasks/${t.id}/comments`, { comment: comment.trim() }); setComment(''); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const uploadProof = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await api.post(`/tasks/${t.id}/comments`, { comment: `📎 Proof uploaded: ${f.name}`, file_url: r.data.url });
      onChanged();
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setBusy(false); e.target.value = ''; }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: '1px dashed #eee' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Update progress">
        <span style={{ fontSize: 10, color: '#888', fontWeight: 700 }}>PROGRESS</span>
        <input type="range" min="0" max="100" step="5" defaultValue={t.progress_pct}
          onMouseUp={async e => { await api.patch(`/tasks/${t.id}/progress`, { progress_pct: e.target.value }).catch(reportError('save the progress')); onChanged(); }}
          onTouchEnd={async e => { await api.patch(`/tasks/${t.id}/progress`, { progress_pct: e.target.value }).catch(reportError('save the progress')); onChanged(); }}
          style={{ width: 110 }} />
        <span style={{ fontSize: 11, color: '#888', width: 32 }}>{t.progress_pct}%</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 220 }}>
        <input style={{ flex: 1, padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, minWidth: 0 }}
          placeholder="💬 Add a quick comment…" value={comment} onChange={e => setComment(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && postComment()} />
        <button disabled={busy || !comment.trim()} onClick={postComment}
          style={{ ...btnPrimary, padding: '6px 12px', background: comment.trim() ? '#3B3A8F' : '#e5e7eb', color: comment.trim() ? 'white' : '#aaa' }}>Post</button>
      </div>
      <label style={{ ...btnPrimary, padding: '6px 12px', background: '#EEEDF8', color: '#3B3A8F', border: '1px dashed #3B3A8F' }} title="Upload proof file">
        {busy ? '…' : '📎 Upload Proof'}
        <input type="file" accept="image/*,.pdf,.xlsx,.doc,.docx" style={{ display: 'none' }} onChange={uploadProof} />
      </label>
      <button onClick={() => onOpen(t.id)} style={{ ...btnPrimary, padding: '6px 12px', background: '#f0f2f8', color: '#555', border: '1px solid #ddd' }} title="Full thread & history">🧵 Thread</button>
      <button onClick={onComplete} style={{ ...btnPrimary, padding: '6px 14px', background: '#166534' }}>✓ Mark Complete</button>
    </div>
  );
}

// ── My Tasks tab — doer actions inline ───────────────────────────────────────
function MyTasks({ rows, onChanged, onOpen }) {
  const [completing, setCompleting] = useState(null);
  const active = rows.filter(t => !['audited', 'cancelled'].includes(t.status));
  const done = rows.filter(t => ['audited', 'cancelled'].includes(t.status));
  return (
    <div>
      {active.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: '#aaa' }}>No open tasks. 🎉</div>}
      {active.map(t => (
        <div key={t.id} style={{ border: '1px solid #eee', borderLeft: `4px solid ${(PRIO[t.priority] || PRIO.medium).c}`, borderRadius: 8, padding: '12px 16px', marginBottom: 10, background: 'white' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ cursor: 'pointer' }} onClick={() => onOpen(t.id)}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                <span style={{ fontFamily: 'monospace', color: '#3B3A8F', marginRight: 8 }}>{t.task_no}</span>{t.title}
                <span style={{ ...tag(PRIO[t.priority] || PRIO.medium), marginLeft: 8 }}>{(PRIO[t.priority] || {}).label}</span>
                <span style={{ ...tag(STATUS_TAG[t.status] || STATUS_TAG.pending), marginLeft: 6 }}>{(STATUS_TAG[t.status] || {}).label}</span>
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                By {t.assigned_by_name || '—'} · Deadline {fmtDT(t.deadline)} · <DaysLeft t={t} />{t.gpo_no ? <> · GPO <strong style={{ color: '#E22D2D' }}>{t.gpo_no}</strong></> : null}
              </div>
              {t.status === 'sent_back' && t.audit_remarks && <div style={{ marginTop: 6, fontSize: 12, color: '#A32D2D', background: '#FCEBEB', padding: '5px 10px', borderRadius: 6 }}>↩ Sent back: {t.audit_remarks}</div>}
            </div>
          </div>
          {['pending', 'in_progress', 'sent_back'].includes(t.status) && (
            <TaskQuickActions t={t} onChanged={onChanged} onComplete={() => setCompleting(t)} onOpen={onOpen} />
          )}
        </div>
      ))}
      {done.length > 0 && (
        <details style={{ marginTop: 14 }}>
          <summary style={{ fontSize: 12, fontWeight: 700, color: '#888', cursor: 'pointer' }}>Closed tasks ({done.length})</summary>
          {done.map(t => (
            <div key={t.id} onClick={() => onOpen(t.id)} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #f5f5f5', fontSize: 12, cursor: 'pointer' }}>
              <span><strong style={{ fontFamily: 'monospace', color: '#3B3A8F' }}>{t.task_no}</strong> {t.title}</span>
              <span>{t.task_score !== null && t.status === 'audited' ? <strong style={{ color: '#166534' }}>{t.task_score}/100 {'★'.repeat(t.quality_rating || 0)}</strong> : (STATUS_TAG[t.status] || {}).label}</span>
            </div>
          ))}
        </details>
      )}
      {completing && <CompleteModal t={completing} onClose={() => setCompleting(null)} onSaved={() => { setCompleting(null); onChanged(); }} />}
    </div>
  );
}

function CompleteModal({ t, onClose, onSaved }) {
  const [note, setNote] = useState('');
  const [proofs, setProofs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const needsProof = ['critical', 'high'].includes(t.priority);
  const submit = async () => {
    setBusy(true); setError('');
    try { await api.patch(`/tasks/${t.id}/complete`, { completion_note: note, completion_proof_urls: proofs }); onSaved(); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
      <div style={{ background: 'white', borderRadius: 12, width: 460, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>✓ Mark Complete — {t.task_no}</h3></div>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#888' }}>{t.title}</p>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <label style={labelStyle}>Completion Note *</label>
        <textarea style={{ ...inputStyle, height: 64, resize: 'vertical' }} value={note} onChange={e => setNote(e.target.value)} />
        <div style={{ marginTop: 10 }}>
          <label style={labelStyle}>Proof Upload {needsProof ? '* (required for ' + t.priority + ' priority)' : '(optional)'}</label>
          <FileUploadBtn multiple label="📎 Upload proof" onUploaded={urls => setProofs(p => [...p, ...urls])} />
          <ProofThumbs urls={proofs} />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={submit} style={{ ...btnPrimary, background: '#166534' }}>{busy ? 'Saving…' : '✓ Mark Complete'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Pending Audit card ────────────────────────────────────────────────────────
function AuditCard({ t, onDone, onOpen }) {
  const [rating, setRating] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (rating >= 1) api.get(`/tasks/${t.id}/score-preview?rating=${rating}`).then(r => setPreview(r.data.data)).catch(() => {});
    else setPreview(null);
  }, [rating, t.id]);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.patch(`/tasks/${t.id}/audit`, { action, quality_rating: rating || undefined, audit_remarks: remarks || undefined });
      onDone();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const proofs = typeof t.completion_proof_urls === 'string' ? JSON.parse(t.completion_proof_urls || '[]') : (t.completion_proof_urls || []);
  return (
    <div style={{ border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', background: 'white' }}>
      <div onClick={onOpen} style={{ cursor: 'pointer' }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>
          <span style={{ fontFamily: 'monospace', color: '#3B3A8F', marginRight: 6 }}>{t.task_no}</span>{t.title}
          <span style={{ ...tag(PRIO[t.priority] || PRIO.medium), marginLeft: 6 }}>{(PRIO[t.priority] || {}).label}</span>
        </div>
        <div style={{ fontSize: 11, color: '#888', marginTop: 3 }}>{t.assigned_to_name} · completed {fmtDT(t.completed_at)} · deadline {fmtDT(t.deadline)}{t.sent_back_count > 0 ? ` · ↩ sent back ×${t.sent_back_count}` : ''}</div>
        {t.completion_note && <div style={{ marginTop: 6, fontSize: 12, background: '#F7F6F3', borderRadius: 6, padding: '6px 10px' }}>{t.completion_note}</div>}
        <ProofThumbs urls={proofs} />
      </div>
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Stars value={rating} onChange={setRating} />
          {preview && <span style={{ fontSize: 12, fontWeight: 800, color: preview.score >= 80 ? '#166534' : preview.score >= 60 ? '#9A6B00' : '#A32D2D' }}>Score: {preview.score}/100</span>}
        </div>
        {preview && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{preview.reason}</div>}
        <input style={{ ...inputStyle, marginTop: 8 }} placeholder="Audit remarks (required for Send Back)…" value={remarks} onChange={e => setRemarks(e.target.value)} />
        <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
          <button disabled={busy} onClick={() => act('send_back')} style={{ ...btnPrimary, background: '#E22D2D' }}>↩ Send Back</button>
          <button disabled={busy || !rating} title={rating ? '' : 'Select a star rating first'} onClick={() => act('accept')}
            style={{ ...btnPrimary, background: rating ? '#166534' : '#e5e7eb', color: rating ? 'white' : '#aaa' }}>✓ Accept</button>
        </div>
      </div>
    </div>
  );
}

// ── New Task modal ────────────────────────────────────────────────────────────
function NewTaskModal({ assignees, gpos, onClose, onSaved }) {
  const [form, setForm] = useState({ priority: 'medium', assigned_to_ids: [] });
  const [attachFiles, setAttachFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!form.title || !(form.assigned_to_ids || []).length) { setError('Title and at least one assignee are required'); return; }
    setBusy(true); setError('');
    try {
      const r = await api.post('/tasks', form);
      const created = r.data.data || [];
      // Upload once, link to every doer's copy: primary task + siblings.
      if (attachFiles.length && created.length) {
        const fd = new FormData();
        attachFiles.forEach(f => fd.append('files', f));
        if (created.length > 1) fd.append('sibling_task_ids', JSON.stringify(created.slice(1).map(t => t.id)));
        try {
          await api.post(`/tasks/${created[0].id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (ae) {
          alert('Task(s) created, but attaching files failed: ' + (ae.response?.data?.error || ae.message) + '\nYou can add them from the task detail.');
        }
      }
      onSaved();
    }
    catch (err) { setError(err.response?.data?.error || 'Failed to create task'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2000, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 12, width: '92%', maxWidth: 540, margin: 'auto', padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ background: '#2D2C72', margin: '-22px -22px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>＋ New Task</h3></div>
        {error && <div style={{ background: '#FCEBEB', color: '#A32D2D', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 12 }}>{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Title *</label>
              <MicButton onAppend={txt => setForm(f => ({ ...f, title: (f.title && f.title.trim() ? f.title.trimEnd() + ' ' : '') + txt }))} />
            </div>
            <input style={inputStyle} value={form.title || ''} onChange={e => setForm({ ...form, title: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Description</label>
              <MicButton onAppend={txt => setForm(f => ({ ...f, description: (f.description && f.description.trim() ? f.description.trimEnd() + ' ' : '') + txt }))} />
            </div>
            <textarea style={{ ...inputStyle, height: 56, resize: 'vertical' }} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Assign To *{(form.assigned_to_ids || []).length > 0 && <span style={{ color: '#2D2C72', fontWeight: 700, textTransform: 'none' }}> · {(form.assigned_to_ids || []).length} selected → {(form.assigned_to_ids || []).length} task{(form.assigned_to_ids || []).length > 1 ? 's' : ''}</span>}</label>
            <div style={{ border: '1px solid #ddd', borderRadius: 6, maxHeight: 150, overflowY: 'auto' }}>
              {assignees.length === 0 && <div style={{ padding: '8px 9px', fontSize: 12, color: '#999' }}>No assignees available</div>}
              {assignees.map(u => {
                const sel = (form.assigned_to_ids || []).includes(u.id);
                return (
                  <div key={u.id} onClick={() => { const cur = form.assigned_to_ids || []; setForm({ ...form, assigned_to_ids: sel ? cur.filter(x => x !== u.id) : [...cur, u.id] }); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px', cursor: 'pointer', fontSize: 12, background: sel ? '#EEEDF8' : '#fff', borderBottom: '1px solid #f4f4f4' }}>
                    <span style={{ width: 15, height: 15, borderRadius: 3, border: `1.5px solid ${sel ? '#2D2C72' : '#bbb'}`, background: sel ? '#2D2C72' : '#fff', color: '#fff', fontSize: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{sel ? '✓' : ''}</span>
                    <span>{u.name} <span style={{ color: '#999' }}>({u.role})</span></span>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>Each selected person gets their own independent copy of this task.</div>
          </div>
          <div><label style={labelStyle}>Priority</label>
            <select style={inputStyle} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}</select></div>
          <div><label style={labelStyle}>Category</label>
            <select style={inputStyle} value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })}>
              <option value="">—</option>{CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
          <div><label style={labelStyle}>Deadline</label><input type="datetime-local" style={inputStyle} value={form.deadline || ''} onChange={e => setForm({ ...form, deadline: e.target.value })} /></div>
          <div><label style={labelStyle}>Estimated Hours</label><input type="number" step="0.5" style={inputStyle} value={form.estimated_hours || ''} onChange={e => setForm({ ...form, estimated_hours: e.target.value })} /></div>
          <div><label style={labelStyle}>Link to GPO (optional)</label>
            <select style={inputStyle} value={form.gpo_no || ''} onChange={e => setForm({ ...form, gpo_no: e.target.value })}>
              <option value="">—</option>{gpos.map(g => <option key={g.id} value={g.gpo_no}>{g.gpo_no} · {g.style_no || ''}</option>)}</select></div>
          <div style={{ gridColumn: '1 / -1' }}><label style={labelStyle}>Reference File (optional)</label>
            <FileUploadBtn label={form.reference_file_url ? '📎 Replace file' : '📎 Attach reference file'} onUploaded={urls => setForm({ ...form, reference_file_url: urls[0] })} />
            {form.reference_file_url && <a href={fileHref(form.reference_file_url)} target="_blank" rel="noreferrer" style={{ marginLeft: 8, fontSize: 12, color: '#166534', fontWeight: 600 }}>✅ attached</a>}</div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Attachments for the doer(s){attachFiles.length > 0 && <span style={{ color: '#2D2C72', fontWeight: 700, textTransform: 'none' }}> · {attachFiles.length} file{attachFiles.length > 1 ? 's' : ''}</span>}</label>
            <AttachmentDropzone files={attachFiles} setFiles={setAttachFiles} />
            {(form.assigned_to_ids || []).length > 1 && attachFiles.length > 0 && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>These files will be attached to all {(form.assigned_to_ids || []).length} doer copies.</div>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnPrimary, background: '#f0f2f8', color: '#444', border: '1px solid #ddd' }}>Cancel</button>
          <button disabled={busy} onClick={save} style={btnPrimary}>{busy ? (attachFiles.length ? 'Creating & uploading…' : 'Creating…') : '＋ Create & Notify'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Task Detail modal ─────────────────────────────────────────────────────────
function TaskDetailModal({ id, user, isMgmt, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [comment, setComment] = useState('');
  const [commentFile, setCommentFile] = useState('');
  const [rating, setRating] = useState(0);
  const [remarks, setRemarks] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [attFiles, setAttFiles] = useState([]);
  const [attBusy, setAttBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);
  // A refusal is said in words (step 1b): "You do not have access to this task", never a blank screen.
  const load = useCallback(() => api.get('/tasks/' + id).then(r => { setT(r.data.data); setLoadErr(null); }).catch(e => setLoadErr(e.response?.data?.error || 'This task could not be opened.')), [id]);
  const loadAtt = useCallback(() => api.get(`/tasks/${id}/attachments`).then(r => setAttachments(r.data.data || [])).catch(() => setAttachments([])), [id]);
  useEffect(() => { load(); loadAtt(); }, [load, loadAtt]);
  useEffect(() => {
    if (isMgmt && rating >= 1 && t?.status === 'completed')
      api.get(`/tasks/${id}/score-preview?rating=${rating}`).then(r => setPreview(r.data.data)).catch(() => {});
  }, [rating, id, isMgmt, t?.status]);

  const addComment = async () => {
    if (!comment && !commentFile) return;
    await api.post(`/tasks/${id}/comments`, { comment, file_url: commentFile || undefined }).catch(e => alert(e.response?.data?.error || 'Failed'));
    setComment(''); setCommentFile(''); load();
  };
  const audit = async (action) => {
    setBusy(true);
    try { await api.patch(`/tasks/${id}/audit`, { action, quality_rating: rating || undefined, audit_remarks: remarks || undefined }); onChanged(); onClose(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const uploadAtt = async () => {
    if (!attFiles.length) return;
    setAttBusy(true);
    try {
      const fd = new FormData(); attFiles.forEach(f => fd.append('files', f));
      await api.post(`/tasks/${id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttFiles([]); loadAtt();
    } catch (err) { alert('Upload failed: ' + (err.response?.data?.error || err.message)); }
    finally { setAttBusy(false); }
  };
  const deleteAtt = async (attId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try { await api.delete(`/tasks/${id}/attachments/${attId}`); loadAtt(); }
    catch (err) { alert(err.response?.data?.error || 'Failed to delete'); }
  };

  if (!t && loadErr) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2050 }}>
      <div style={{ background: 'white', borderRadius: 14, padding: '20px 24px', maxWidth: 420, boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#A32D2D', marginBottom: 8 }}>{loadErr}</div>
        <button onClick={onClose} style={{ padding: '7px 16px', background: '#3B3A8F', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>Close</button>
      </div>
    </div>);
  if (!t) return null;
  const canEditAtt = isMgmt || t.assigned_by === user?.id;
  const proofs = typeof t.completion_proof_urls === 'string' ? JSON.parse(t.completion_proof_urls || '[]') : (t.completion_proof_urls || []);
  const kv = (k, v) => <div style={{ fontSize: 12 }}><span style={{ color: '#888' }}>{k}: </span><strong>{v ?? '—'}</strong></div>;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 2050, overflowY: 'auto', padding: '24px 0' }}>
      <div style={{ background: 'white', borderRadius: 14, width: '92%', maxWidth: 720, margin: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ background: '#2D2C72', padding: '14px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.72)', textTransform: 'uppercase', letterSpacing: 1 }}>{t.task_no} · {(PRIO[t.priority] || {}).label} · {(STATUS_TAG[t.status] || {}).label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'white', marginTop: 2 }}>{t.title}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)', fontSize: 22, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={{ padding: '16px 22px 22px' }}>
          {t.description && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#444' }}>{t.description}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '6px 14px', marginBottom: 10 }}>
            {kv('Assigned To', t.assigned_to_name)}{kv('Assigned By', t.assigned_by_name)}{kv('Category', CAT[t.category] || '—')}
            {kv('Deadline', fmtDT(t.deadline))}{kv('Est. Hours', t.estimated_hours)}{kv('GPO', t.gpo_no)}
            {t.task_score !== null && kv('Score', `${t.task_score}/100`)}{t.quality_rating && kv('Rating', '★'.repeat(t.quality_rating))}
            {t.sent_back_count > 0 && kv('Sent Back', `×${t.sent_back_count}`)}
          </div>
          <ProgressBar pct={t.progress_pct} />
          {t.reference_file_url && <div style={{ marginTop: 8 }}><a href={fileHref(t.reference_file_url)} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#3B3A8F', fontWeight: 600 }}>📎 Reference file</a></div>}

          {/* Attachments */}
          <div style={{ marginTop: 16, fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #EEEDF8', paddingBottom: 4 }}>📎 Attachments {attachments.length > 0 && `(${attachments.length})`}</div>
          {attachments.length === 0 ? <div style={{ fontSize: 12, color: '#aaa', padding: '8px 0' }}>No attachments.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
              {attachments.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#F7F6F3', borderRadius: 6, padding: '6px 10px', fontSize: 12 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {a.file_name} <span style={{ color: '#999' }}>({fmtSize(a.file_size)}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''})</span></span>
                  <span style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
                    <span onClick={() => downloadAttachment(id, a)} style={{ cursor: 'pointer', color: '#3B3A8F', fontWeight: 600 }} title="Download">⬇ Download</span>
                    {canEditAtt && <span onClick={() => deleteAtt(a.id)} style={{ cursor: 'pointer', color: '#A32D2D', fontWeight: 700 }} title="Delete">✕</span>}
                  </span>
                </div>
              ))}
            </div>}
          {canEditAtt && (
            <div style={{ marginTop: 8 }}>
              <AttachmentDropzone files={attFiles} setFiles={setAttFiles} />
              {attFiles.length > 0 && (
                <div style={{ marginTop: 8, textAlign: 'right' }}>
                  <button disabled={attBusy} onClick={uploadAtt} style={btnPrimary}>{attBusy ? 'Uploading…' : `⬆ Upload ${attFiles.length} file${attFiles.length > 1 ? 's' : ''}`}</button>
                </div>
              )}
            </div>
          )}
          {t.completion_note && <div style={{ marginTop: 10, fontSize: 12, background: '#F7F6F3', borderRadius: 6, padding: '8px 12px' }}><strong>Completion note:</strong> {t.completion_note}<ProofThumbs urls={proofs} /></div>}
          {t.audit_remarks && <div style={{ marginTop: 8, fontSize: 12, background: t.status === 'sent_back' ? '#FCEBEB' : '#EAF7EE', borderRadius: 6, padding: '8px 12px' }}><strong>Audit:</strong> {t.audit_remarks}</div>}

          {/* Audit section (management, completed tasks) */}
          {isMgmt && t.status === 'completed' && (
            <div style={{ marginTop: 14, border: '2px solid #3B3A8F', borderRadius: 10, padding: '12px 16px', background: '#FBFBFE' }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#3B3A8F', marginBottom: 8 }}>🔍 AUDIT</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <Stars value={rating} onChange={setRating} />
                {preview && <span style={{ fontSize: 13, fontWeight: 800 }}>Score preview: <span style={{ color: preview.score >= 80 ? '#166534' : preview.score >= 60 ? '#9A6B00' : '#A32D2D' }}>{preview.score}/100</span></span>}
              </div>
              {preview && <div style={{ fontSize: 10, color: '#888', marginTop: 3 }}>{preview.reason}</div>}
              <input style={{ ...inputStyle, marginTop: 8 }} placeholder="Audit remarks…" value={remarks} onChange={e => setRemarks(e.target.value)} />
              <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                <button disabled={busy} onClick={() => audit('send_back')} style={{ ...btnPrimary, background: '#E22D2D' }}>↩ Send Back</button>
                <button disabled={busy || !rating} title={rating ? '' : 'Select a star rating first'} onClick={() => audit('accept')} style={{ ...btnPrimary, background: rating ? '#166534' : '#e5e7eb', color: rating ? 'white' : '#aaa' }}>✓ Accept</button>
              </div>
            </div>
          )}

          {/* Comments */}
          <div style={{ marginTop: 16, fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, borderBottom: '1px solid #EEEDF8', paddingBottom: 4 }}>💬 Comments</div>
          {(t.comments || []).length === 0 ? <div style={{ fontSize: 12, color: '#aaa', padding: '8px 0' }}>No comments yet.</div>
            : t.comments.map(c => (
              <div key={c.id} style={{ padding: '8px 0', borderBottom: '1px solid #f5f5f5', fontSize: 12 }}>
                <strong>{c.user_name || '—'}</strong> <span style={{ color: '#aaa', fontSize: 10 }}>{fmtDT(c.created_at)}</span>
                <div style={{ marginTop: 2 }}>{c.comment}{c.file_url && <a href={fileHref(c.file_url)} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#3B3A8F', fontWeight: 600 }}>📎 file</a>}</div>
              </div>
            ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <input style={inputStyle} placeholder="Add a comment…" value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && addComment()} />
            <FileUploadBtn label={commentFile ? '✅' : '📎'} onUploaded={urls => setCommentFile(urls[0])} />
            <button onClick={addComment} style={btnPrimary}>Post</button>
          </div>

          {/* History */}
          <details style={{ marginTop: 14 }}>
            <summary style={{ fontSize: 12, fontWeight: 700, color: '#888', cursor: 'pointer' }}>🕘 History ({(t.history || []).length})</summary>
            {(t.history || []).map(h => (
              <div key={h.id} style={{ fontSize: 11, color: '#666', padding: '5px 0', borderBottom: '1px solid #f5f5f5' }}>
                <strong>{fmtDT(h.created_at)}</strong> · {h.from_status ? `${h.from_status} → ` : ''}{h.to_status} · {h.changed_by_name || '—'}{h.note ? ` — ${h.note}` : ''}
              </div>
            ))}
          </details>
        </div>
      </div>
    </div>
  );
}
